import { createSelector } from 'reselect';
import { fallbackToEmptyArray, EMPTY_ARRAY } from '../utils/fallbackHelper';
import type { ShippingOptionsState, ShippingOptionCaveat } from '../../types';

const SHIPPING_PRICING_SUCCESS_STATUS = 'success';

const selectHasSelectedShippingMethod = (
  shippingOptions: ShippingOptionsState
) => !!shippingOptions?.selectedShippingMethodId;

const selectSelectedShippingMethodId = (
  shippingOptions: ShippingOptionsState
) => (shippingOptions ? shippingOptions.selectedShippingMethodId : undefined);

const selectSelectedShippingMethod = (shippingOptions: ShippingOptionsState) =>
  shippingOptions &&
  shippingOptions.selectedShippingMethodId &&
  shippingOptions.shippingOptions
    ? shippingOptions.shippingOptions.find(
        option =>
          option.shippingMethodId === shippingOptions.selectedShippingMethodId
      )
    : undefined;

const selectShippingOptionName = (shippingOptions: ShippingOptionsState) =>
  (shippingOptions && shippingOptions.selectedShippingMethodName) || undefined;

const selectSelectedShippingOptionPromisedArrivalDate = (
  shippingOptions: ShippingOptionsState
) => selectSelectedShippingMethod(shippingOptions)?.promisedArrivalDate;

const selectShippingOptionsToDisplay = createSelector(
  (shippingOptions: ShippingOptionsState) => shippingOptions,
  (shippingOptions: ShippingOptionsState) =>
    // filter out any options that are not successfully priced
    fallbackToEmptyArray(
      shippingOptions.shippingOptions?.filter(
        so =>
          (so.pricingData?.pricingStatus?.toLowerCase() || '') ===
          SHIPPING_PRICING_SUCCESS_STATUS
      )
    )
);

const selectHasShippingOptionsToDisplay = createSelector(
  (shippingOptions: ShippingOptionsState) => shippingOptions,
  (shippingOptions: ShippingOptionsState) =>
    Boolean(selectShippingOptionsToDisplay(shippingOptions).length)
);

// TODO:: unused, remove
const selectItemCaveatsWithErrors = createSelector(
  (shippingOptions: ShippingOptionsState) => shippingOptions,
  (shippingOptions: ShippingOptionsState) =>
    fallbackToEmptyArray(
      shippingOptions?.caveats?.itemCaveats?.filter(
        (item: ShippingOptionCaveat) =>
          Object.values(item).filter(error => error === true).length > 0
      )
    )
);

// TODO:: unused, remove
const selectItemCaveatsErrors = createSelector(
  (shippingOptions: ShippingOptionsState) => shippingOptions,
  (shippingOptions: ShippingOptionsState) => {
    if (shippingOptions?.caveats?.itemCaveats) {
      const itemErrors: any[] = [];
      shippingOptions.caveats.itemCaveats.forEach((i: any) => {
        itemErrors.push(Object.keys(i).filter(key => i[key] === true));
      });
      return [...new Set(itemErrors.flat())];
    }
    return EMPTY_ARRAY;
  }
);

const selectIsSelectedShippingMethodInShippingQuotes = createSelector(
  (shippingOptions: ShippingOptionsState) => shippingOptions,
  (shippingOptions: ShippingOptionsState) => {
    const selectedShippingMethodId =
      selectSelectedShippingMethodId(shippingOptions);
    return shippingOptions.shippingOptions?.some(
      shippingOption =>
        shippingOption.shippingMethodId === selectedShippingMethodId
    );
  }
);

const selectRetrievalStatus = (shippingOptions: ShippingOptionsState) =>
  shippingOptions.retrievalStatus;

export default {
  selectHasSelectedShippingMethod,
  selectSelectedShippingMethodId,
  selectSelectedShippingMethod,
  selectShippingOptionName,
  selectSelectedShippingOptionPromisedArrivalDate,
  selectHasShippingOptionsToDisplay,
  selectShippingOptionsToDisplay,
  selectItemCaveatsWithErrors,
  selectItemCaveatsErrors,
  selectIsSelectedShippingMethodInShippingQuotes,
  selectRetrievalStatus,
};
